import React, { useState } from "react";
import { TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { METRIC_UNITS, Question, Questionnaire, US_CUSTOMARY_UNITS } from "./models";
import RequiredAsterisk from "./RequiredAsterisk";

const InputWeightView = ({ question, questionnaire, onChange, pattern }: { question: Question, questionnaire: Questionnaire, onChange: (question: Question) => void, pattern?: string }) => {
  // Only allow valid float numbers (positive, decimal)
  const floatPattern = pattern || "^\\d*(\\.\\d*)?$";
  const regExp = new RegExp(floatPattern);
  const [weightError, setWeightError] = useState("");

  // Determine unit: '1' for kg, '2' for lbs
  const unit = question.unit || US_CUSTOMARY_UNITS;

  // Conversion constants
  const LBS_TO_KG = 0.453592;
  const KG_TO_LBS = 2.20462;
  // Conversion helpers
  const lbsToKg = (lbs: number) => Math.round(lbs * LBS_TO_KG);
  const kgToLbs = (kg: number) => Math.round(kg * KG_TO_LBS);

  const { t } = useTranslation(questionnaire.cohort);
  // Display value in correct unit
  let displayValue = '';
  if (question.value && !isNaN(Number(question.value))) {
    const storedLbs = Number(question.value);
    displayValue = unit === METRIC_UNITS ? lbsToKg(storedLbs).toString() : storedLbs.toString();
  }

  // Handle input change, always store in lbs
  const handleOnChange = (value: string) => {
    if (value === "") {
      setWeightError("");
      onChange({ ...question, value: "" });
      return;
    }
    if (!regExp.test(value)) {
      setWeightError("Please enter a valid number.");
      return;
    }
    const numValue = Number(value);
    const max = unit === METRIC_UNITS ? 635 : 1400;
    if (isNaN(numValue) || numValue < 0 || numValue > max) {
      setWeightError(`Please enter a value between 0 and ${max}.`);
      return;
    }
    setWeightError("");
    const updatedLbs = unit === METRIC_UNITS ? kgToLbs(numValue).toString() : value;
    onChange({ ...question, value: updatedLbs });
  };

  return (
    <div>
      <Typography variant="h6" component='label' htmlFor={`${question.key}`} id={`question-${question.key}-label`}>
        {t(question.question)}
        {question.required && <RequiredAsterisk />}
      </Typography>
      <TextField
        type="text"
        placeholder={t(question.placeholder || (unit === METRIC_UNITS ? "Enter weight in kg" : "Enter weight in lbs"))}
        value={displayValue}
        onChange={(e) => handleOnChange(e.target.value)}
        error={!!weightError}
        aria-labelledby={`question-${question.key}-label`}
        helperText={weightError || (question.hint ? t(question.hint) : undefined)}
        fullWidth
        id={`${question.key}`}
      />
    </div>
  );
}

export default InputWeightView;
